<template>
  <!-- Full styled button — only when panel is closed -->
  <button
    v-if="!open"
    class="panel-toggle"
    @click="open = true"
    title="Adatok megnyitása"
  >
    <span class="panel-toggle__icon">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/>
        <line x1="8" y1="2" x2="8" y2="18"/>
        <line x1="16" y1="6" x2="16" y2="22"/>
      </svg>
    </span>
    <span class="panel-toggle__label">Adatrétegek</span>
  </button>

  <!-- Slide-in drawer -->
  <transition name="drawer">
    <div v-if="open" class="right-drawer">
      <div class="drawer-header">
        <div class="drawer-header__title">
          <span class="drawer-title">Adatok</span>
          <span v-if="activeField" class="drawer-subtitle">· {{ activeField.name }}</span>
        </div>
        <button class="collapse-btn" title="Bezárás" @click="open = false">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </button>
      </div>

      <n-tabs
        :key="tabsKey"
        v-model:value="activeTab"
        type="segment"
        size="small"
        class="drawer-tabs"
        pane-class="drawer-pane"
      >
        <n-tab-pane name="layers" tab="Rétegek">
          <LayersPanel :layer-tree="layerTree" :loading="loading" :error="error" />
        </n-tab-pane>

        <n-tab-pane v-if="availableYears.length > 1" name="years" tab="Évek">
          <YearFilter :years="availableYears" />
        </n-tab-pane>

        <n-tab-pane v-if="fields.length" name="fields" tab="Parcellák">
          <FieldSelector
            :fields="fields"
            :loading="loading"
            :error="error"
            :active-field-id="activeField?.id ?? null"
            @select="onSelectField"
            @zoom-to="zoomToExtent"
          />
        </n-tab-pane>
      </n-tabs>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { NTabs, NTabPane } from 'naive-ui'
import type { LayerTreeNode } from '@/types/layerTree'
import { useFarms, type FieldSummary } from '@/composables/useFarms'
import { useLayerSelection } from '@/composables/useLayerSelection'
import LayersPanel from './LayersPanel.vue'
import YearFilter from './YearFilter.vue'
import FieldSelector from './FieldSelector.vue'

const props = defineProps<{
  layerTree: LayerTreeNode[]
  loading: boolean
  error: string | null
}>()

const { activeFarm, fields, activeField, selectField } = useFarms()
const { zoomToExtent } = useLayerSelection()

const open = ref(false)
const activeTab = ref<'layers' | 'years' | 'fields'>('layers')

// Remount the tabs whenever the farm or the set of visible tabs changes, so the
// segment capsule recomputes its position/width instead of lingering stale.
const tabsKey = computed(
  () => `${activeFarm.value?.id ?? 'none'}-${availableYears.value.length > 1}-${fields.value.length > 0}`,
)

function onSelectField(field: FieldSummary) {
  selectField(field.id)
}

// Collect all distinct years across the tree to decide whether the Years tab shows.
const availableYears = computed<number[]>(() => {
  const acc = new Set<number>()
  collectYears(props.layerTree, acc)
  return [...acc].sort((a, b) => b - a)
})

function collectYears(nodes: LayerTreeNode[], acc: Set<number>) {
  for (const node of nodes) {
    if (node.type === 'group') collectYears(node.children, acc)
    else if (node.type === 'raster_type') node.cogs.forEach(c => acc.add(new Date(c.sensing_date).getFullYear()))
    else if (node.type === 'vector') node.vector_layers.forEach(vl => acc.add(vl.year))
  }
}

// If the active tab's content disappears (e.g. switching to a farm without
// fields or years), snap back to the always-present Layers tab.
watch([() => fields.value.length, availableYears], () => {
  if (activeTab.value === 'fields' && !fields.value.length) activeTab.value = 'layers'
  if (activeTab.value === 'years' && availableYears.value.length <= 1) activeTab.value = 'layers'
})
</script>

<style scoped>
/* ── Toggle button ──────────────────────────────────────────────────────── */
.panel-toggle {
  position: fixed;
  top: 72px;
  right: 16px;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 8px 14px 8px 10px;
  background: #1e1e2e;
  border: 1px solid #313244;
  border-radius: 8px;
  color: #cdd6f4;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  box-shadow: 0 2px 12px rgba(0,0,0,0.4);
  transition: background 0.15s, border-color 0.15s;
}
.panel-toggle:hover {
  background: #313244;
  border-color: #89b4fa;
  color: #89b4fa;
}
.panel-toggle__icon { display: flex; align-items: center; }

/* ── Drawer ─────────────────────────────────────────────────────────────── */
.right-drawer {
  position: fixed;
  top: 70px;
  right: 16px;
  z-index: 19;
  width: 320px;
  max-height: calc(100vh - 90px);
  background: rgba(16,16,32,0.85);
  backdrop-filter: blur(6px);
  border: 1px solid #313244;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.5);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 12px 8px 16px;
  flex-shrink: 0;
}
.drawer-header__title {
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
}
.drawer-title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #89b4fa;
}
.drawer-subtitle {
  font-size: 12px;
  font-weight: 600;
  color: #a6e3a1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── Collapse button ────────────────────────────────────────────────────── */
.collapse-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  background: none;
  border: 1px solid #313244;
  border-radius: 6px;
  color: #6c7086;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s, background 0.15s;
}
.collapse-btn:hover { color: #89b4fa; border-color: #89b4fa; background: rgba(137,180,250,0.08); }

/* ── Tabs ───────────────────────────────────────────────────────────────── */
.drawer-tabs {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 0 12px 12px;
}
:deep(.drawer-pane) {
  overflow-y: auto;
  max-height: calc(100vh - 210px);
  padding-top: 10px !important;
}

/* ── Naive UI overrides ─────────────────────────────────────────────────── */
:deep(.n-tabs-rail) { background: rgba(49,50,68,0.5) !important; }
:deep(.n-tabs-tab) { color: #a6adc8 !important; }
:deep(.n-tabs-tab--active) { color: #1e1e2e !important; font-weight: 600; }
:deep(.n-tabs .n-tabs-capsule) { background: #89b4fa !important; }

/* ── Drawer slide transition ────────────────────────────────────────────── */
.drawer-enter-active,
.drawer-leave-active { transition: opacity 0.2s, transform 0.2s; }
.drawer-enter-from,
.drawer-leave-to { opacity: 0; transform: translateX(16px); }
</style>
